import { useRef, type FunctionComponent, type PropsWithChildren } from 'react';
import cx from 'classnames';
import { makeList } from '../../helpers';
import useNextBox from '../../hooks/useNextBox';
import useSubscriptionPlanPrices from '../../hooks/useSubscriptionPlanPrices';
import { LEARN_MORE } from '../../utils/legalUrls';
import { Plans } from '../constants';
import { annualPerks, seasonalPerks } from './constants';
import TrialUpgradeDisclaimer from './TrialUpgradeDisclaimer/TrialUpgradeDisclaimer';
import bestValuePng from './images/badge-best-value@2x.png';
import boxesImg from './images/img-boxes@2x.jpg';
import shapeSvg from './images/shape.svg';

export const INSTANT_SAVINGS_AMOUNT = 60;

type DesktopLayoutProps = {
  handleChangeActive: (code: string) => void;
  active: string;
  showAdditionalPerks: boolean;
  onClickUpgrade: () => void;
};

const DesktopLayout: FunctionComponent<PropsWithChildren<DesktopLayoutProps>> = ({
  handleChangeActive = () => null,
  active = Plans.SEASONAL,
  showAdditionalPerks = false,
  onClickUpgrade,
  children,
}) => {
  const { seasonalPrice, annualPrice } = useSubscriptionPlanPrices();
  const seasonalList = makeList(seasonalPerks, [], showAdditionalPerks);
  const annualList = makeList(annualPerks, [], showAdditionalPerks);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const nextBox = useNextBox();

  const handleSelectPlanAndScroll = (plan: string) => {
    handleChangeActive(plan);
    confirmRef.current &&
      confirmRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
  };

  return (
    <>
      <section className="trialUpgrade__container" style={{ backgroundImage: `url(${boxesImg})` }}>
        <section className="trialUpgrade">
          <div className="trialUpgrade__right">
            <div className="trialUpgrade__title">Ready to Start Your Membership?</div>
            <div className="trialUpgrade__subtitle">
              Starting your membership early ensures you start with the box you want.
            </div>
            <div className="trialUpgrade__buttons" data-testid="trialUpgrade-buttons">
              <div
                className={cx('trialUpgrade__buttons__box', {
                  active: active === Plans.SEASONAL,
                })}
                role="button"
                tabIndex={0}
                onClick={() => handleChangeActive(Plans.SEASONAL)}
                onKeyDown={() => handleChangeActive(Plans.SEASONAL)}
                data-testid="trialUpgrade-buttons-seasonal"
              >
                {active === Plans.SEASONAL && (
                  <div className="activeCheck" data-testid="activeCheck">
                    <img src={shapeSvg} alt="active" />
                  </div>
                )}
                <div className="trialUpgrade__buttons__title">SEASONAL</div>
                {seasonalPrice && (
                  <div className="trialUpgrade__buttons__price">{`$${seasonalPrice}`}</div>
                )}
                <div className="trialUpgrade__buttons__type">Standard</div>
              </div>
              <div
                className={cx('trialUpgrade__buttons__box', {
                  active: active === Plans.ANNUAL,
                })}
                role="button"
                tabIndex={0}
                onClick={() => handleChangeActive(Plans.ANNUAL)}
                onKeyDown={() => handleChangeActive(Plans.ANNUAL)}
                data-testid="trialUpgrade-buttons-annual"
              >
                {active === Plans.ANNUAL && (
                  <div className="activeCheck" data-testid="activeCheck">
                    <img src={shapeSvg} alt="active" />
                  </div>
                )}
                <div className="trialUpgrade__buttons__title">ANNUAL</div>
                {annualPrice && (
                  <div className="trialUpgrade__buttons__price spaced">
                    {`$${annualPrice} USD!`}
                  </div>
                )}
              </div>
            </div>
            <div className="trialUpgrade__next">
              <div className="trialUpgrade__next__title">
                {nextBox ? `Next Box: ${nextBox}` : ''}
              </div>
              <TrialUpgradeDisclaimer selected={active} />
              <div className="trialUpgrade__next__button">
                <button
                  data-testid="confirm-purchase"
                  type="button"
                  className="fffBtnPrimary"
                  onClick={() => onClickUpgrade()}
                  ref={confirmRef}
                >
                  CONFIRM PURCHASE
                </button>
                {children}
              </div>
              <div className="trialUpgrade__next__additional">
                *Additional $8/box shipping for Canada, Hawaii, Alaska, and Puerto Rico. Sales tax
                may apply (
                <a href={LEARN_MORE} target="_blank" rel="noreferrer">
                  learn more
                </a>
                ).
              </div>
            </div>
          </div>
        </section>
      </section>
      <section className="trialUpgrade-detail">
        <p className="trialUpgrade-detail__title">Let’s Get Started</p>
        <div className="trialUpgrade-detail__cards">
          <div className="trialUpgrade-detail__card">
            <div className="trialUpgrade-detail__card__title">Seasonal</div>
            {seasonalPrice && (
              <div className="trialUpgrade-detail__card__price">
                <span className="sign">$</span>
                {seasonalPrice}
                <span className="unit">/ box</span>
              </div>
            )}
            <div className="trialUpgrade-detail__card__detail">
              Pay Per Seasonal Box
              <br />+ Free Shipping!
            </div>
            <div className="trialUpgrade-detail__card__checklist">
              <ul className="planOptions">
                {seasonalList.map(({ label, check }) => (
                  <li key={label} className={cx({ strikeThrough: !check })}>
                    <span>{label}</span>
                  </li>
                ))}
              </ul>
            </div>
            <button
              data-testid="select-seasonal"
              className="fffBtnPrimary"
              type="button"
              onClick={() => handleSelectPlanAndScroll(Plans.SEASONAL)}
            >
              SELECT SEASONAL
            </button>
          </div>
          <div className="trialUpgrade-detail__card">
            <img className="trialUpgrade-detail__best-value" src={bestValuePng} alt="best value" />
            <div className="trialUpgrade-detail__card__title">Annual</div>
            {annualPrice && (
              <div className="trialUpgrade-detail__card__price">
                <span className="sign">$</span>
                {annualPrice}
                <span className="unit">/ year</span>
              </div>
            )}
            <div className="trialUpgrade-detail__card__detail">
              ${INSTANT_SAVINGS_AMOUNT} Instant Savings
              <br />
              When you Prepay for 4 Boxes
              <br />+ Free Shipping!
            </div>
            <div className="trialUpgrade-detail__card__checklist">
              <ul className="planOptions">
                {annualList.map(({ label }) => (
                  <li key={label}>{label}</li>
                ))}
              </ul>
            </div>
            <button
              data-testid="select-annual"
              className="fffBtnPrimary"
              type="button"
              onClick={() => handleSelectPlanAndScroll(Plans.ANNUAL)}
            >
              SELECT ANNUAL
            </button>
          </div>
        </div>
      </section>
    </>
  );
};

export default DesktopLayout;
